import { Routes } from '@angular/router';

import { authGuard } from './core/guards/auth.guard';
import { COMMS_ROLES, OPS_ROLES, PERSON_CREATION_ROLES, ROLES } from './core/auth/role-tiers';
import { roleGuard } from './core/guards/role.guard';
import { navOnlyPathsHomeGuard } from './core/navigation/nav-only-paths';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { CAPABILITY_KEYS } from './core/capabilities/capabilities.model';
import {
  FEATURE_UNAVAILABLE_PATH,
  requireCapability,
} from './core/capabilities/require-capability.guard';

export const routes: Routes = [
  {
    path: 'login',
    loadComponent: () => import('./features/auth/login/login').then((m) => m.Login),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/shell/shell').then((m) => m.Shell),
    children: [
      {
        path: '',
        canActivate: [navOnlyPathsHomeGuard],
        data: { title: 'Panel de Administración' },
        loadComponent: () => import('./features/dashboard/dashboard').then((m) => m.Dashboard),
      },
      {
        // Las colas pasan a ser tabs de una misma pantalla (pedido explícito 2026-08-05):
        // ya no hay un paso intermedio de "elegí una cola" antes de ver el trabajo real.
        // Pendientes SB se mudó al shell de Centro operativo (S16c) — la primera cola que queda
        // acá es Revisión manual.
        path: 'solicitudes',
        canActivate: [roleGuard],
        data: { roles: OPS_ROLES },
        loadComponent: () =>
          import('./features/solicitudes/solicitudes-shell/solicitudes-shell').then(
            (m) => m.SolicitudesShell,
          ),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'revision-manual' },
          {
            path: 'revision-manual',
            data: { title: 'Revisión manual' },
            loadComponent: () =>
              import('./features/solicitudes/revision-manual/manual-review-table/revision-manual-table').then(
                (m) => m.RevisionManualTable,
              ),
          },
          {
            path: 'rechazadas',
            data: { title: 'Rechazadas' },
            loadComponent: () =>
              import('./features/solicitudes/rechazadas/rejected-table/rechazadas-table').then(
                (m) => m.RechazadasTable,
              ),
          },
          {
            path: 'aprobadas',
            data: { title: 'Aprobadas' },
            loadComponent: () =>
              import('./features/solicitudes/aprobadas/approved-table/aprobadas-table').then(
                (m) => m.AprobadasTable,
              ),
          },
        ],
      },
      {
        // Redirect (no remoción — bookmark/notificación existentes no deben romperse): Pendientes
        // SB se mudó al shell de Centro operativo (S16c). Debe declararse ANTES de 'solicitudes/:ref'
        // en este array: de lo contrario ese param route (que matchea cualquier segmento) la
        // capturaría primero como si "pendientes-sb" fuera un externalReference.
        path: 'solicitudes/pendientes-sb',
        pathMatch: 'full',
        redirectTo: '/centro-operativo/pendientes-sb',
      },
      {
        path: 'solicitudes/:ref',
        canActivate: [roleGuard],
        data: { title: 'Detalle de solicitud', roles: OPS_ROLES },
        loadComponent: () =>
          import('./features/solicitudes/detalle/application-detail').then(
            (m) => m.ApplicationDetail,
          ),
      },
      {
        // Aprobadas por excepción (ADR 0022): informe de solo lectura. Solo ADMIN, igual que el CS
        // (RoleMatrix); pantalla propia porque Solicitudes y Centro operativo son OPS. La URL
        // /excepciones se conserva para no romper enlaces.
        path: 'excepciones',
        canActivate: [roleGuard],
        data: { title: 'Aprobadas por excepción', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/excepciones/exceptions-table/excepciones-table').then(
            (m) => m.ExcepcionesTable,
          ),
      },
      {
        path: 'analiticas',
        canActivate: [roleGuard],
        // OPERATOR pierde Analíticas (pedido explícito 2026-08-26); METRICS_VIEWER la conserva
        // porque ya la tenía (sin roleGuard antes, era accesible para cualquier rol autenticado).
        data: { title: 'Analíticas', roles: [ROLES.ADMIN, ROLES.METRICS_VIEWER] },
        loadComponent: () => import('./features/analiticas/analiticas').then((m) => m.Analiticas),
      },
      {
        path: 'evaluaciones-bcra',
        canActivate: [roleGuard],
        data: { title: 'Evaluaciones BCRA', roles: OPS_ROLES },
        loadComponent: () =>
          import('./features/evaluaciones-bcra/bcra-evaluations-table/evaluaciones-bcra-table').then(
            (m) => m.EvaluacionesBcraTable,
          ),
      },
      {
        path: 'pruebas',
        canActivate: [roleGuard],
        // Pruebas pasa a ser ADMIN-only (pedido explícito 2026-08-26).
        data: { title: 'Pruebas', roles: [ROLES.ADMIN] },
        loadComponent: () => import('./features/pruebas/pruebas').then((m) => m.Pruebas),
      },
      {
        path: 'reglas',
        canActivate: [roleGuard],
        data: { title: 'Reglas de Aceptación', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/rule-sets/rule-sets-table/rule-sets-table').then(
            (m) => m.RuleSetsTable,
          ),
      },
      {
        path: 'reglas/nuevo',
        canActivate: [roleGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { title: 'Nuevo conjunto de reglas', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/rule-sets/rule-set-form/rule-set-form').then((m) => m.RuleSetForm),
      },
      {
        path: 'reglas/:id',
        canActivate: [roleGuard],
        data: { title: 'Detalle de reglas', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/rule-sets/rule-set-detail/rule-set-detail').then(
            (m) => m.RuleSetDetail,
          ),
      },
      {
        path: 'formularios',
        canActivate: [roleGuard],
        data: { title: 'Configuración de formularios', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/form-configs/form-configs-table/form-configs-table').then(
            (m) => m.FormConfigsTable,
          ),
      },
      {
        path: 'formularios/nuevo',
        canActivate: [roleGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { title: 'Nueva configuración de formulario', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/form-configs/form-config-form/form-config-form').then(
            (m) => m.FormConfigForm,
          ),
      },
      {
        path: 'formularios/:id',
        canActivate: [roleGuard],
        data: { title: 'Detalle de configuración de formulario', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/form-configs/form-config-detail/form-config-detail').then(
            (m) => m.FormConfigDetail,
          ),
      },
      {
        path: 'formularios/:id/editar',
        canActivate: [roleGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { title: 'Editar configuración de formulario', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/form-configs/form-config-form/form-config-form').then(
            (m) => m.FormConfigForm,
          ),
      },
      {
        path: 'grupos-cobro',
        canActivate: [roleGuard],
        // The side panel is a form: leaving it with changes asks first (universal table, like Usuarios).
        canDeactivate: [unsavedChangesGuard],
        data: { title: 'Grupos de cobranza', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/collection-groups/collection-groups-list/collection-groups-list').then(
            (m) => m.CollectionGroupsList,
          ),
      },
      {
        // ADR 0021, 2026-10-12 amendment ("grupos por tipo dentro de cada área"): SMS, WhatsApp,
        // Envíos programados and Filtrar Excel are sidebar rows now, so the channel picker that
        // used to live here (2026-08-25) was retired: it duplicated the menu. Old links and
        // bookmarks to /comunicacion land on SMS; its own roleGuard (COMMS_ROLES) still applies.
        path: 'comunicacion',
        pathMatch: 'full',
        redirectTo: '/comunicacion/sms',
      },
      {
        path: 'comunicacion/filtrar-excel',
        canActivate: [roleGuard],
        data: { roles: COMMS_ROLES },
        loadComponent: () =>
          import('./features/comunicacion/excel-filter/excel-filter-shell/excel-filter-shell').then(
            (m) => m.ExcelFilterShell,
          ),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'filtrar' },
          {
            path: 'filtrar',
            data: { title: 'Filtrar Excel' },
            loadComponent: () =>
              import('./features/comunicacion/excel-filter/excel-filter-create/excel-filter-create').then(
                (m) => m.ExcelFilterCreate,
              ),
          },
          {
            // Reglas on the universal table, shared with the SMS and WhatsApp rules (same config,
            // `data.channel` picks the gateway). Read is OPS (COMMS_OPERATOR included, same as the
            // parent `comunicacion/filtrar-excel`); write stays ADMIN only inside the screen and on
            // the server.
            path: 'reglas',
            data: { title: 'Reglas para filtrar Excel', channel: 'excel' },
            loadComponent: () =>
              import('./features/comunicacion/shared/send-rules/send-rules-table').then(
                (m) => m.SendRulesTable,
              ),
          },
        ],
      },
      {
        path: 'comunicacion/sms',
        canActivate: [roleGuard],
        data: { roles: COMMS_ROLES },
        loadComponent: () =>
          import('./features/comunicacion/sms/sms-shell/sms-shell').then((m) => m.SmsShell),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'campanas' },
          {
            path: 'campanas',
            data: { title: 'Campañas de SMS' },
            loadComponent: () =>
              import('./features/comunicacion/sms/sms-campaigns-table/sms-campaigns-table').then(
                (m) => m.SmsCampaignsTable,
              ),
          },
          {
            path: 'nueva',
            data: { title: 'Nueva campaña de SMS' },
            loadComponent: () =>
              import('./features/comunicacion/sms/sms-campaign-create/sms-campaign-create').then(
                (m) => m.SmsCampaignCreate,
              ),
          },
          {
            path: 'filtrar-excel',
            pathMatch: 'full',
            redirectTo: '/comunicacion/filtrar-excel/filtrar',
          },
          {
            path: 'individual',
            data: { title: 'Envío individual' },
            loadComponent: () =>
              import('./features/comunicacion/sms/sms-individual-send/sms-individual-send').then(
                (m) => m.SmsIndividualSend,
              ),
          },
          {
            // Reglas on the universal table, shared with WhatsApp and Excel (`data.channel` picks
            // the gateway). SMS stays ADMIN-only end to end, unlike the other two channels —
            // COMMS_OPERATOR never reaches this route.
            path: 'reglas',
            canActivate: [roleGuard],
            data: { title: 'Reglas SMS', roles: [ROLES.ADMIN], channel: 'sms' },
            loadComponent: () =>
              import('./features/comunicacion/shared/send-rules/send-rules-table').then(
                (m) => m.SendRulesTable,
              ),
          },
          {
            path: 'plantillas',
            canActivate: [roleGuard],
            data: { title: 'Plantillas SMS', roles: [ROLES.ADMIN] },
            loadComponent: () =>
              import('./features/comunicacion/sms/sms-templates-table/sms-templates-table').then(
                (m) => m.SmsTemplatesTable,
              ),
          },
          { path: 'configuracion', pathMatch: 'full', redirectTo: 'reglas' },
          { path: 'envio-masivo', pathMatch: 'full', redirectTo: 'nueva' },
          { path: 'cuenta', pathMatch: 'full', redirectTo: 'campanas' },
        ],
      },
      {
        // WAD (WhatsApp masivo) shell (PR3, 2026-08-25): esta fase entrega selector de canal +
        // shell + ruteo; las pantallas reales llegan fase por fase (ver
        // sdd/wad-admin-frontend/tasks). Plantillas es visible a OPERATOR igual que ADMIN
        // (Requirement "Plantillas tab (read-only, OPERATOR-visible)") — a diferencia de SMS, acá
        // ninguna sub-ruta tiene un roleGuard ADMIN-only propio.
        // Phase 4 (2026-08-25) reemplazó `WadPlaceholder` por las pantallas reales de Plantillas y
        // Cuenta. Phase 5 (2026-08-25) reemplazó `WadPlaceholder` por las pantallas reales de
        // Campañas + historial. Phase 6 (2026-08-25) reemplazó el último placeholder, Nueva
        // Campaña — el componente `WadPlaceholder` se eliminó del todo (sin más usos en el árbol
        // de rutas), a diferencia de los redirects de arriba, que sí se conservan por bookmarks.
        path: 'comunicacion/wad',
        canActivate: [roleGuard],
        data: { roles: COMMS_ROLES },
        loadComponent: () =>
          import('./features/comunicacion/wad/wad-shell/wad-shell').then((m) => m.WadShell),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'campanas' },
          {
            path: 'campanas',
            data: { title: 'Campañas de WhatsApp' },
            loadComponent: () =>
              import('./features/comunicacion/wad/wad-campaigns-table/wad-campaigns-table').then(
                (m) => m.WadCampaignsTable,
              ),
          },
          {
            // D2 (QA 2026-10-04): a campaign detail belongs to Historial (the shell highlights the
            // tab whose path prefixes the URL). The old path keeps working for links and
            // bookmarks (Envíos programados, Campañas); the router keeps the query params.
            path: 'campanas/:id',
            redirectTo: 'historial/:id',
          },
          {
            path: 'nueva',
            data: { title: 'Nueva campaña de WhatsApp' },
            loadComponent: () =>
              import('./features/comunicacion/wad/wad-campaign-create/wad-campaign-create').then(
                (m) => m.WadCampaignCreate,
              ),
          },
          {
            // Reglas on the universal table, shared with SMS and Excel (`data.channel` picks the
            // gateway). COMMS_OPERATOR reaches this route (read is OPS on the server) and sees the
            // same panel read-only.
            path: 'reglas',
            canActivate: [roleGuard],
            data: { title: 'Reglas WAD', roles: COMMS_ROLES, channel: 'wad' },
            loadComponent: () =>
              import('./features/comunicacion/shared/send-rules/send-rules-table').then(
                (m) => m.SendRulesTable,
              ),
          },
          {
            // "Historial" (wad-reglas-motor PR5): el ledger de solo-lectura que antes vivía en
            // "Campañas" (`WadCampaignList`, `GET /admin/wad/campaigns`) — sin cambios de
            // comportamiento, solo reubicado bajo esta ruta nueva porque "Campañas" ahora es la
            // bandeja de PLANES (`WadCampaignList` reescrito, `GET /admin/wad/campaign-plans`).
            // El detalle de cada campaña vive en `historial/:id` (abajo); `campanas/:id` redirige.
            path: 'historial',
            data: { title: 'Historial de envíos de WhatsApp' },
            loadComponent: () =>
              import('./features/comunicacion/wad/wad-history-table/wad-history-table').then(
                (m) => m.WadHistoryTable,
              ),
          },
          {
            // The recipients of a campaign.
            path: 'historial/:id',
            data: { title: 'Detalle de campaña de WhatsApp' },
            loadComponent: () =>
              import('./features/comunicacion/wad/wad-recipients-table/wad-recipients-table').then(
                (m) => m.WadRecipientsTable,
              ),
          },
          {
            // Plantillas: read-only catalog, ADMIN and COMMS_OPERATOR (OPERATOR too, unchanged —
            // no extra roleGuard, same as today).
            path: 'plantillas',
            data: { title: 'Plantillas de WhatsApp' },
            loadComponent: () =>
              import('./features/comunicacion/wad/wad-templates-table/wad-templates-table').then(
                (m) => m.WadTemplatesTable,
              ),
          },
          // C7 audit (2026-10-05): the Cuenta tab was retired; the balance is a small line above
          // Campañas. Old links and bookmarks land on Campañas.
          { path: 'cuenta', pathMatch: 'full', redirectTo: 'campanas' },
        ],
      },
      {
        // "Envíos programados" (envios-programados PR4, 2026-08-28): bandeja única (sin sub-tabs,
        // a diferencia de los shells de SMS/WAD) de `scheduled_send` para ambos canales. Tier COMMS
        // del RoleMatrix (roadmap #14): ADMIN + COMMS_OPERATOR, igual que sus hermanos
        // `comunicacion/sms` y `comunicacion/wad`. OPERATOR sigue sin acceso (2026-08-26) aunque el
        // backend le permita las lecturas.
        path: 'comunicacion/envios-programados',
        canActivate: [roleGuard],
        data: { roles: COMMS_ROLES, title: 'Envíos programados' },
        loadComponent: () =>
          import('./features/comunicacion/envios-programados/scheduled-sends-table/scheduled-sends-table').then(
            (m) => m.ScheduledSendsTable,
          ),
      },
      {
        // Historial retirado de la barra de tabs (2026-08-24, ver comentario en sms-shell.ts):
        // hallazgo confirmado contra el PDF real del vendor — obtener_respuestaapi_bloque.asp no
        // soporta rango desde/hasta ni expone destino/texto, y la app no persiste el idinterno
        // necesario para reconstruirla. Redirect (no remoción de la ruta hija, que ya no existe
        // en `comunicacion/sms`): un deep-link/bookmark viejo cae en la sub-pestaña por defecto,
        // igual que los redirects mensajeria/* de abajo.
        path: 'comunicacion/sms/historial',
        pathMatch: 'full',
        redirectTo: '/comunicacion/sms/campanas',
      },
      {
        // Redirects permanentes (no remoción — bookmark/notificación existentes no deben
        // romperse): las 3 pantallas se mudaron a sub-tabs del shell de SMS bajo Comunicación Masiva.
        path: 'mensajeria/reglas',
        pathMatch: 'full',
        redirectTo: '/comunicacion/sms/reglas',
      },
      {
        path: 'mensajeria/plantillas',
        pathMatch: 'full',
        redirectTo: '/comunicacion/sms/plantillas',
      },
      {
        path: 'mensajeria/envio-masivo',
        pathMatch: 'full',
        redirectTo: '/comunicacion/sms/nueva',
      },
      {
        path: 'clientes',
        canActivate: [roleGuard],
        // OPERATOR recupera Clientes (pedido explícito 2026-08-28); había pasado a ser
        // ADMIN-only el 2026-08-26 junto con Pruebas/Comunicación Masiva/Exportaciones.
        // Título "Personas" (ADR 0020 de credlap-architecture, fase A): la pantalla ya no llama
        // "cliente" a cualquiera que pidió un crédito.
        data: { title: 'Personas', roles: OPS_ROLES },
        loadComponent: () =>
          import('./features/clientes/persons-table/personas-table').then((m) => m.PersonasTable),
      },
      {
        path: 'posibles-duplicados',
        canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.DUPLICATES)],
        data: { title: 'Posibles duplicados', roles: [ROLES.ADMIN] },
        loadComponent: () => import('./features/duplicados/duplicados').then((m) => m.Duplicados),
      },
      {
        // Requirements 1.17 and 1.26: the doubtful rows of a person import, resolved by an ADMIN.
        path: 'importar-sb',
        canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.SB_IMPORT)],
        data: { title: 'Importar desde SB', roles: [ROLES.ADMIN] },
        loadComponent: () => import('./features/sb-import/sb-import').then((m) => m.SbImport),
      },
      {
        // Requirement 1.21 (decision 12): the web's opt-out requests, resolved by OPS.
        path: 'pedidos-baja',
        canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.PUBLIC_OPT_OUT)],
        data: { title: 'Pedidos de baja', roles: OPS_ROLES },
        loadComponent: () =>
          import('./features/pedidos-baja/pedidos-baja').then((m) => m.PedidosBaja),
      },
      {
        path: 'cola-identidad',
        canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.PERSON_IMPORT)],
        data: { title: 'Cola de identidad', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/cola-identidad/cola-identidad').then((m) => m.ColaIdentidad),
      },
      {
        // Requirement 1.1 "Dar de alta una persona" ("operador y administrador"), behind the ficha
        // edition capability like "Guardar cambios"; before `clientes/:dni` so "nueva" is never a DNI.
        path: 'clientes/nueva',
        canActivate: [
          roleGuard,
          requireCapability(CAPABILITY_KEYS.FICHA_EDITION, { blockPaused: true }),
        ],
        data: { title: 'Dar de alta una persona', roles: PERSON_CREATION_ROLES },
        loadComponent: () =>
          import('./features/clientes/persona-alta/persona-alta').then((m) => m.PersonaAlta),
      },
      {
        path: 'clientes/:dni',
        canActivate: [roleGuard],
        // T2 (roadmap #11): la edición de identidad canónica sigue la convención
        // CanComponentDeactivate de rule-set-form/form-config-form — pide confirmación antes de
        // navegar afuera con el form de identidad sucio sin guardar.
        canDeactivate: [unsavedChangesGuard],
        // QA polish ADR 0020 fase A: la pantalla ya no llama "cliente" a cualquiera que pidió un
        // crédito (título de la página = h1 del topbar, shell.ts deepestTitle()).
        data: { title: 'Legajo de la persona', roles: OPS_ROLES },
        loadComponent: () =>
          import('./features/clientes/cliente-detalle/cliente-detalle').then(
            (m) => m.ClienteDetalle,
          ),
      },
      {
        path: 'exportaciones',
        canActivate: [roleGuard],
        // Exportaciones pasa a ser ADMIN-only (pedido explícito 2026-08-26).
        data: { title: 'Exportaciones', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/exportaciones/exportaciones-landing/exportaciones-landing').then(
            (m) => m.ExportacionesLanding,
          ),
      },
      {
        path: 'exportaciones/logs',
        canActivate: [roleGuard],
        data: { title: 'Explorador de logs', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/exportaciones/logs/logs-table/logs-table').then((m) => m.LogsTable),
      },
      {
        path: 'exportaciones/logs/trace/:correlationId',
        canActivate: [roleGuard],
        data: { title: 'Trace de log', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/exportaciones/trace/log-trace').then((m) => m.LogTrace),
      },
      {
        path: 'exportaciones/backups',
        canActivate: [roleGuard],
        data: { title: 'Backups', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/exportaciones/backups/backups-table/backups-table').then(
            (m) => m.BackupsTable,
          ),
      },
      {
        // single-debitable (ADR 0019 etapa 1): diferencias entre el régimen debitable de AD y la
        // elegibilidad de Cartera, para que el dueño las revise antes de unificarlas. Solo ADMIN
        // (DNI, nombres y CBU de las dos bases).
        path: 'exportaciones/diferencias-debitable',
        canActivate: [roleGuard],
        data: { title: 'Diferencias de régimen debitable', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/exportaciones/diferencias-debitable/debit-differences-table/debit-differences-table').then(
            (m) => m.DebitDifferencesTable,
          ),
      },
      {
        path: 'exportaciones/historial',
        canActivate: [roleGuard],
        data: { title: 'Historial de exportaciones', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/exportaciones/historial/exports-history-table/exports-history-table').then(
            (m) => m.ExportsHistoryTable,
          ),
      },
      {
        // Centro operativo (S16c): unifica Alertas, Pendientes SB y Documentos abandonados en un
        // mismo shell de tabs — decisión de UX aprobada 2026-08-10 (Opción C, "shell con tabs de
        // tipo mixto", ver docs/DISENO-UX-CENTRO-ACCION-UNIFICADO.md). Default: Alertas, por ser
        // la cola más urgente (feed en vivo).
        path: 'centro-operativo',
        canActivate: [roleGuard],
        data: { roles: OPS_ROLES },
        loadComponent: () =>
          import('./features/accion-operativa/accion-operativa-shell/accion-operativa-shell').then(
            (m) => m.AccionOperativaShell,
          ),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'alertas' },
          {
            path: 'alertas',
            data: { title: 'Alertas' },
            loadComponent: () =>
              import('./features/alertas/alerts-table/alertas-table').then((m) => m.AlertasTable),
          },
          {
            path: 'pendientes-sb',
            data: { title: 'Pendientes de envío a SB' },
            loadComponent: () =>
              import('./features/solicitudes/pendientes-sb/pending-sb-table/pendientes-sb-table').then(
                (m) => m.PendientesSbTable,
              ),
          },
          {
            path: 'documentos-abandonados',
            data: { title: 'Documentos abandonados' },
            loadComponent: () =>
              import('./features/documentos-abandonados/abandoned-documents-table/documentos-abandonados-table').then(
                (m) => m.DocumentosAbandonadosTable,
              ),
          },
          {
            // ADR 0030 section 7: "Mensajería CS↔PF". ADMIN only, like CS (RoleMatrix CONFIG), and
            // only while CS publishes the `messaging.monitor` capability (ADR 0031; CS answers 404
            // otherwise). The role is checked first: a non-ADMIN gets 403, never the capability page.
            path: 'mensajeria',
            canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.MESSAGING_MONITOR)],
            data: { title: 'Mensajería CS↔PF', roles: [ROLES.ADMIN] },
            loadComponent: () =>
              import('./features/accion-operativa/mensajeria/messaging-monitor').then(
                (m) => m.MessagingMonitor,
              ),
          },
        ],
      },
      {
        // Redirects (no remoción — bookmark/notificación existentes no deben romperse): estas 2
        // pantallas se mudaron a tabs del shell de Centro operativo (S16c).
        path: 'alertas',
        pathMatch: 'full',
        redirectTo: '/centro-operativo/alertas',
      },
      {
        path: 'documentos-abandonados',
        pathMatch: 'full',
        redirectTo: '/centro-operativo/documentos-abandonados',
      },
      {
        path: 'auditoria',
        canActivate: [roleGuard],
        // Solo ADMIN: mismo gate que el backend (AuditEventsAdminController, "actividad
        // administrativa cruzada, más sensible que el detalle de una solicitud puntual").
        data: { title: 'Auditoría', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/auditoria/audit-table/auditoria-table').then((m) => m.AuditoriaTable),
      },
      {
        path: 'configuracion',
        canActivate: [roleGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { title: 'Configuración', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/configuracion/general/configuracion-general').then(
            (m) => m.ConfiguracionGeneral,
          ),
      },
      {
        path: 'configuracion/usuarios',
        canActivate: [roleGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { title: 'Usuarios del panel', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/configuracion/usuarios/users-table/usuarios-table').then(
            (m) => m.UsuariosTable,
          ),
      },
      {
        // Panel de cron jobs (solo lectura, GET /admin/schedulers/status): misma forma de
        // navegación que 'configuracion/usuarios' — ruta hermana enlazada desde el header de
        // Configuración, no un rail item más del form de ajustes (esto es monitoreo, no un
        // ajuste editable, no comparte el FormGroup de configuracion-general). ADMIN-only acá
        // para mantener el mismo criterio de acceso de toda la sección Configuración, aunque el
        // backend también permite OPERATOR.
        path: 'configuracion/cron-jobs',
        canActivate: [roleGuard],
        data: { title: 'Cron jobs', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/configuracion/cron-jobs-table/cron-jobs-table').then(
            (m) => m.CronJobsTable,
          ),
      },
      {
        // Requirement 1.33: the "No disponible" reason catalog. ADMIN only (CS RoleMatrix CONFIG
        // for writes) and only while CS publishes `stage1.unavailable-reason` (404 otherwise).
        path: 'configuracion/motivos-no-disponible',
        canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.UNAVAILABLE_REASON)],
        data: { title: 'Motivos "No disponible"', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/configuracion/motivos-no-disponible/motivos-no-disponible').then(
            (m) => m.MotivosNoDisponible,
          ),
      },
      {
        // Requirement 1.17: the person origin catalog. ADMIN only (CS RoleMatrix CONFIG for writes)
        // and only while CS publishes `stage1.person-origin` (404 otherwise).
        path: 'configuracion/requisitos-documentales',
        canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.DOCUMENT_MANAGER)],
        data: { roles: [ROLES.ADMIN], title: 'Requisitos documentales' },
        loadComponent: () =>
          import('./features/configuracion/document-rules/document-rules').then(
            (m) => m.DocumentRules,
          ),
      },
      {
        path: 'configuracion/comercializadores',
        canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.COMERCIALIZADORES)],
        data: { title: 'Comercializadores', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/configuracion/comercializadores/comercializadores').then(
            (m) => m.Comercializadores,
          ),
      },
      {
        path: 'configuracion/campos-personalizados',
        canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.CUSTOM_FIELDS)],
        data: { title: 'Campos personalizados', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/configuracion/custom-fields/custom-fields').then(
            (m) => m.CustomFields,
          ),
      },
      {
        path: 'configuracion/origenes-persona',
        canActivate: [roleGuard, requireCapability(CAPABILITY_KEYS.PERSON_ORIGIN)],
        data: { title: 'Orígenes de la persona', roles: [ROLES.ADMIN] },
        loadComponent: () =>
          import('./features/configuracion/origenes-persona/origenes-persona').then(
            (m) => m.OrigenesPersona,
          ),
      },
      {
        path: 'mi-cuenta',
        data: { title: 'Mi cuenta' },
        loadComponent: () => import('./features/mi-cuenta/mi-cuenta').then((m) => m.MiCuenta),
      },
      {
        // ADR 0031 section 3.3: where `requireCapability` sends a direct URL of a feature CS
        // reports DISABLED (or PAUSED, for routes that only start new work).
        path: FEATURE_UNAVAILABLE_PATH,
        data: { title: 'Función no disponible' },
        loadComponent: () =>
          import('./shared/feature-unavailable/feature-unavailable').then(
            (m) => m.FeatureUnavailable,
          ),
      },
      {
        path: '403',
        data: { title: 'Acceso denegado' },
        loadComponent: () =>
          import('./shared/access-denied/access-denied').then((m) => m.AccessDenied),
      },
      {
        // UI audit 2 (X1): unknown URLs render a not-found page inside the shell (it used to be a
        // silent top-level redirect to the dashboard). Must stay the LAST child: every explicit
        // route and legacy redirect above wins first. Being under the shell, authGuard still sends
        // an unauthenticated visitor to /login.
        path: '**',
        data: { title: 'Página no encontrada' },
        loadComponent: () => import('./features/not-found/not-found').then((m) => m.NotFound),
      },
    ],
  },
];
